/**
 * Serialization between persisted copilot API records and the assistant-ui
 * thread model, plus the drawer's conversation-summary shapes.
 */

import type { ThreadMessageLike } from "@assistant-ui/react";
import type {
  CopilotMessageRecord,
  CopilotThreadRecord,
} from "../../lib/copilotApi";

export type SavedConversation = {
  id: string;
  title: string;
  createdAt: Date;
};

export const LAST_OPENED_THREAD_STORAGE_KEY = "copilot:last-opened-thread-id";

export function toThreadSummary(t: CopilotThreadRecord) {
  return {
    id: t.id,
    title: t.title,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
  };
}

function extractTextPayload(payload: unknown): string {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") {
    const text = (payload as Record<string, unknown>).text;
    if (typeof text === "string") return text;
  }
  return "";
}

function extractReasoningPayload(payload: unknown): string {
  if (payload && typeof payload === "object") {
    const reasoning = (payload as Record<string, unknown>).reasoning;
    if (typeof reasoning === "string") return reasoning;
  }
  return "";
}

// Deep-analysis findings appended onto the suspension turn by the Go
// analysis-stream proxy; rendered as a separate text part AFTER the MCAP
// approval card so re-hydration matches the live streaming order.
function extractAnalysisPayload(payload: unknown): string {
  if (payload && typeof payload === "object") {
    const analysis = (payload as Record<string, unknown>).analysis_text;
    if (typeof analysis === "string") return analysis;
  }
  return "";
}

function extractAnalysisReasoningPayload(payload: unknown): string {
  if (payload && typeof payload === "object") {
    const reasoning = (payload as Record<string, unknown>).analysis_reasoning;
    if (typeof reasoning === "string") return reasoning;
  }
  return "";
}

function extractCreditUsage(usage: unknown, legacyPayload: unknown) {
  if ((!usage || typeof usage !== "object") && legacyPayload) {
    usage = (legacyPayload as Record<string, unknown>).token_usage;
  }
  if (!usage || typeof usage !== "object") return undefined;
  const record = usage as Record<string, unknown>;
  const creditsConsumed = Number(record.credits_consumed) || 0;
  return creditsConsumed > 0
    ? {
        creditsConsumed,
        creditBalance: 0,
      }
    : undefined;
}

function extractSuspensionAttachment(
  attachments: unknown,
): unknown | undefined {
  if (!Array.isArray(attachments)) return undefined;
  const item = attachments.find(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      (entry as Record<string, unknown>).type === "mcap_suspension",
  ) as Record<string, unknown> | undefined;
  return item?.payload;
}

/**
 * Recovers the incident/device identifiers the workflow persisted in its
 * context snapshot, so the agent context survives a page refresh.
 */
export function extractContextSnapshot(state: unknown): {
  incidentId?: string;
  incidentReference?: string;
  deviceId?: string;
  deviceName?: string;
} {
  const snapshot = (state as { context_snapshot?: unknown } | undefined)
    ?.context_snapshot;
  if (!snapshot || typeof snapshot !== "object") return {};
  const record = snapshot as Record<string, unknown>;
  const incidentId =
    typeof record.incident_id === "string" && record.incident_id
      ? record.incident_id
      : undefined;
  const deviceId =
    typeof record.device_id === "string" && record.device_id
      ? record.device_id
      : undefined;
  const suspension =
    record.suspension_payload && typeof record.suspension_payload === "object"
      ? (record.suspension_payload as Record<string, unknown>)
      : undefined;
  const triage =
    record.triage_context && typeof record.triage_context === "object"
      ? (record.triage_context as Record<string, unknown>)
      : undefined;
  const device =
    triage?.device && typeof triage.device === "object"
      ? (triage.device as Record<string, unknown>)
      : undefined;
  const incidentReference = [
    record.incident_reference,
    suspension?.incident_reference,
    triage?.incident_reference,
  ].find(
    (value): value is string => typeof value === "string" && Boolean(value),
  );
  const deviceName = [
    record.device_name,
    suspension?.device_name,
    device?.name,
  ].find(
    (value): value is string => typeof value === "string" && Boolean(value),
  );
  return { incidentId, incidentReference, deviceId, deviceName };
}

export function toThreadMessages(
  messages: CopilotMessageRecord[],
): ThreadMessageLike[] {
  return messages.map((m) => {
    const role: "user" | "assistant" | "system" =
      m.role === "tool" ? "assistant" : m.role;
    const content: Array<
      Extract<ThreadMessageLike["content"], readonly unknown[]>[number]
    > = [];
    const reasoning = extractReasoningPayload(m.content_payload);
    const text = extractTextPayload(m.content_payload);

    if (m.role === "assistant") {
      const suspensionPayload = extractSuspensionAttachment(m.attachments);
      const analysisText = extractAnalysisPayload(m.content_payload);
      const analysisReasoning = extractAnalysisReasoningPayload(
        m.content_payload,
      );
      const creditUsage = extractCreditUsage(m.token_usage, m.content_payload);
      if (reasoning) content.push({ type: "reasoning", text: reasoning });
      if (text) content.push({ type: "text", text });
      if (suspensionPayload) {
        // Findings appended after the suspension prove the extraction and
        // deep analysis completed. Stamp that onto the payload so the MCAP
        // approval card settles this specific card even when the workflow
        // node was overwritten by later runs in the same thread.
        const data =
          analysisText && typeof suspensionPayload === "object"
            ? { ...(suspensionPayload as object), analysis_present: true }
            : suspensionPayload;
        content.push({
          type: "data",
          name: "mcap_suspension",
          data,
        });
      }
      if (analysisReasoning) {
        content.push({ type: "reasoning", text: analysisReasoning });
      }
      if (analysisText) content.push({ type: "text", text: analysisText });
      return {
        role,
        content,
        metadata: creditUsage ? { custom: { creditUsage } } : undefined,
      };
    }

    content.push({ type: "text", text });
    // User-message context chips: the `attachments` column stores the exact
    // assistant-ui CompleteAttachment[] shape produced by chipToAttachment(),
    // so it can be handed straight back to ThreadMessageLike.attachments.
    const attachments = Array.isArray(m.attachments)
      ? (m.attachments as ThreadMessageLike["attachments"])
      : undefined;
    return { role, content, attachments };
  });
}

export function toRestoredThreadMessages(
  messages: CopilotMessageRecord[],
): ThreadMessageLike[] {
  return toThreadMessages(messages).map((message, index) => ({
    ...message,
    id: messages[index]?.id,
    createdAt: messages[index]?.created_at
      ? new Date(messages[index].created_at)
      : new Date(),
  }));
}
